/**
 * Comprehensive Natal Chart Calculator
 * 
 * Calculates complete birth chart including:
 * - All planetary positions (Sun, Moon, Mercury, Venus, Mars, Jupiter, Saturn, Uranus, Neptune, Pluto)
 * - Ascendant (Rising Sign)
 * - House system (Placidus)
 * - Aspects between planets
 * 
 * Uses astronomy-calculations.js for professional-grade accuracy with JPL Horizons data.
 * Provides VSOP87-based calculations with retrograde detection and precise velocities.
 */

import {
  calculateJulianDay,
  calculateSunPosition,
  getZodiacSign,
  ZODIAC_SIGNS,
  formatAstrologyDegree
} from './calculator';
import {
  calculateMoonPosition,
  calculateMercuryPosition,
  calculateVenusPosition,
  calculateMarsPosition,
  calculateJupiterPosition,
  calculateSaturnPosition,
  calculateUranusPosition,
  calculateNeptunePosition,
  calculatePlutoPosition
} from './planets-vsop87';
import {
  calculateAscendant,
  calculateHouseCusps,
  getPlanetHouse,
  type HouseSystem
} from './houses';
import { astronomyEngine } from './astronomy-engine';
import { convertBirthTimeToUTC } from '../../utils/timezone';
import type { Planet, ZodiacSign, House, PlanetPosition, Aspect } from '../../types/astrology.types';

// ============================================================================
// PLANETARY POSITION CALCULATIONS
// ============================================================================
// 
// NOTE: Planetary position calculations are now imported from planets-improved.ts
// which provides better accuracy with proper mean anomaly rates and eccentricity handling.
//
// Re-export for backward compatibility
export {
  calculateMoonPosition,
  calculateMercuryPosition,
  calculateVenusPosition,
  calculateMarsPosition,
  calculateJupiterPosition,
  calculateSaturnPosition,
  calculateUranusPosition,
  calculateNeptunePosition,
  calculatePlutoPosition
} from './planets-vsop87';

// ============================================================================
// ASPECT CALCULATIONS
// ============================================================================

/**
 * Calculate aspects between planets
 * 
 * Major aspects:
 * - Conjunction: 0° (orb ±8°)
 * - Opposition: 180° (orb ±8°)
 * - Trine: 120° (orb ±8°)
 * - Square: 90° (orb ±8°)
 * - Sextile: 60° (orb ±6°)
 */
export function calculateAspects(
  planetPositions: Array<{ planet: Planet; longitude: number }>
): Aspect[] {
  const aspects: Aspect[] = [];
  
  for (let i = 0; i < planetPositions.length; i++) {
    for (let j = i + 1; j < planetPositions.length; j++) {
      const planet1 = planetPositions[i];
      const planet2 = planetPositions[j];
      
      // Calculate angular distance
      let angle = Math.abs(planet1.longitude - planet2.longitude);
      if (angle > 180) angle = 360 - angle;
      
      // Check for aspects
      const aspectConfigs = [
        { type: 'conjunction' as const, angle: 0, orb: 8 },
        { type: 'opposition' as const, angle: 180, orb: 8 },
        { type: 'trine' as const, angle: 120, orb: 8 },
        { type: 'square' as const, angle: 90, orb: 8 },
        { type: 'sextile' as const, angle: 60, orb: 6 }
      ];
      
      for (const config of aspectConfigs) {
        const difference = Math.abs(angle - config.angle);
        if (difference <= config.orb) {
          const orb = difference;
          const isApplying = planet2.longitude > planet1.longitude;
          
          aspects.push({
            planet1: planet1.planet,
            planet2: planet2.planet,
            angle: config.angle,
            type: config.type,
            orb,
            isApplying
          });
          break; // Only one aspect per planet pair
        }
      }
    }
  }
  
  return aspects;
}

// ============================================================================
// COMPLETE NATAL CHART
// ============================================================================

export interface CompleteNatalChart {
  birthDate: string;
  birthTime?: string;
  birthLocation?: {
    latitude: number;
    longitude: number;
    city?: string;
  };
  
  // Planetary positions
  planets: PlanetPosition[];
  
  // Ascendant
  ascendant: {
    sign: ZodiacSign;
    degree: number;
    longitude: number;
  };
  
  // Houses
  houses: Array<{
    house: House;
    sign: ZodiacSign;
    degree: number;
    longitude: number;
  }>;
  
  // Aspects
  aspects: Aspect[];
  
  // Elemental balance
  elementalBalance: {
    fire: number;
    earth: number;
    air: number;
    water: number;
  };
  
  // Modality balance
  modalityBalance: {
    cardinal: number;
    fixed: number;
    mutable: number;
  };
}

/**
 * Calculate complete natal chart using astronomy-calculations.js
 * 
 * @param birthDate - Birth date (Date object or ISO string)
 * @param birthTime - Birth time in HH:MM format (local time)
 * @param birthLocation - Birth location with latitude/longitude
 * @param houseSystem - House system to use (default: equal)
 * @returns Complete natal chart with all planetary positions
 */
export async function calculateCompleteNatalChart(
  birthDate: Date | string,
  birthTime?: string,
  birthLocation?: { latitude: number; longitude: number; city?: string },
  houseSystem: HouseSystem = 'equal'
): Promise<CompleteNatalChart> {
  // Convert to UTC Date for accurate calculations
  let date: Date;
  if (typeof birthDate === 'string') {
    date = new Date(birthDate);
  } else {
    // Create new date to avoid mutating original
    date = new Date(birthDate);
  }
  
  // Handle time conversion: birthTime should be LOCAL time at birth location
  // Convert from political timezone (with DST) to UTC for calculations
  if (birthTime) {
    if (birthLocation) {
      console.log('🔍 [NATAL CHART] Birth data received:', {
        originalDate: birthDate,
        birthTime,
        location: {
          lat: birthLocation.latitude,
          lon: birthLocation.longitude,
          city: birthLocation.city
        }
      });
      
      // Use timezone-aware conversion based on coordinates
      // This accounts for political timezones and DST automatically
      date = convertBirthTimeToUTC(
        date,
        birthTime,
        birthLocation.latitude,
        birthLocation.longitude
      );
      
      console.log('🔍 [NATAL CHART] After timezone conversion:', {
        utcDate: date.toISOString(),
        utcYear: date.getUTCFullYear(),
        utcMonth: date.getUTCMonth() + 1,
        utcDay: date.getUTCDate(),
        utcHour: date.getUTCHours(),
        utcMinute: date.getUTCMinutes()
      });
    } else {
      // No location provided, assume time is already in UTC
      const [hours, minutes] = birthTime.split(':').map(Number);
      date.setUTCHours(hours || 0, minutes || 0, 0, 0);
    }
  } else {
    // Default to noon UTC if no time provided
    date.setUTCHours(12, 0, 0, 0);
  }
  
  // Initialize astronomy engine for accurate calculations
  try {
    await astronomyEngine.initialize();
  } catch (error) {
    console.error('Failed to initialize astronomy engine:', error);
    // Fall back to synchronous calculations if engine fails
    return calculateCompleteNatalChartFallback(date, birthTime, birthLocation, houseSystem);
  }

  // Calculate all planetary positions using astronomy-calculations.js
  let planetaryData: Map<string, any>;
  try {
    planetaryData = await astronomyEngine.calculateAllPlanets(date);
    
    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      const jd = (date.getTime() / 86400000) + 2440587.5;
      console.log('🌍 Planetary positions (astronomy-calculations.js):', {
        calculationDate: date.toISOString(),
        julianDay: jd.toFixed(6),
        planets: {
          sun: {
            longitude: planetaryData.get('Sun')?.longitude.toFixed(6),
            sign: planetaryData.get('Sun')?.sign,
            degree: planetaryData.get('Sun')?.degree.toFixed(2),
            retrograde: planetaryData.get('Sun')?.retrograde
          },
          moon: {
            longitude: planetaryData.get('Moon')?.longitude.toFixed(6),
            sign: planetaryData.get('Moon')?.sign,
            degree: planetaryData.get('Moon')?.degree.toFixed(2),
            latitude: planetaryData.get('Moon')?.latitude?.toFixed(6),
            distance: planetaryData.get('Moon')?.distance?.toFixed(2),
            retrograde: planetaryData.get('Moon')?.retrograde
          },
          mercury: planetaryData.get('Mercury')?.longitude.toFixed(2) + '° ' + planetaryData.get('Mercury')?.sign,
          venus: planetaryData.get('Venus')?.longitude.toFixed(2) + '° ' + planetaryData.get('Venus')?.sign,
          mars: planetaryData.get('Mars')?.longitude.toFixed(2) + '° ' + planetaryData.get('Mars')?.sign,
          jupiter: planetaryData.get('Jupiter')?.longitude.toFixed(2) + '° ' + planetaryData.get('Jupiter')?.sign,
          saturn: planetaryData.get('Saturn')?.longitude.toFixed(2) + '° ' + planetaryData.get('Saturn')?.sign,
          uranus: planetaryData.get('Uranus')?.longitude.toFixed(2) + '° ' + planetaryData.get('Uranus')?.sign,
          neptune: planetaryData.get('Neptune')?.longitude.toFixed(2) + '° ' + planetaryData.get('Neptune')?.sign,
          pluto: planetaryData.get('Pluto')?.longitude.toFixed(2) + '° ' + planetaryData.get('Pluto')?.sign
        }
      });
      
      console.log('🌙 MOON ACCURACY CHECK:', {
        utcDateTime: date.toISOString(),
        julianDay: jd.toFixed(6),
        moonLongitude: planetaryData.get('Moon')?.longitude.toFixed(6) + '°',
        moonSign: planetaryData.get('Moon')?.sign,
        moonDegree: planetaryData.get('Moon')?.degree.toFixed(6),
        moonLatitude: planetaryData.get('Moon')?.latitude?.toFixed(6) + '°',
        moonDistance: planetaryData.get('Moon')?.distance?.toFixed(2) + ' AU',
        moonRetrograde: planetaryData.get('Moon')?.retrograde,
        note: 'Compare this to ephemeris data (e.g., astro.com, astro-seek.com)'
      });
    }
  } catch (error) {
    console.error('Failed to calculate planetary positions:', error);
    // Fall back to synchronous calculations if calculation fails
    return calculateCompleteNatalChartFallback(date, birthTime, birthLocation, houseSystem);
  }

  // Extract longitudes for each planet
  const sunData = planetaryData.get('Sun');
  const moonData = planetaryData.get('Moon');
  const mercuryData = planetaryData.get('Mercury');
  const venusData = planetaryData.get('Venus');
  const marsData = planetaryData.get('Mars');
  const jupiterData = planetaryData.get('Jupiter');
  const saturnData = planetaryData.get('Saturn');
  const uranusData = planetaryData.get('Uranus');
  const neptuneData = planetaryData.get('Neptune');
  const plutoData = planetaryData.get('Pluto');

  const sunLong = sunData?.longitude || 0;
  const moonLong = moonData?.longitude || 0;
  const mercuryLong = mercuryData?.longitude || 0;
  const venusLong = venusData?.longitude || 0;
  const marsLong = marsData?.longitude || 0;
  const jupiterLong = jupiterData?.longitude || 0;
  const saturnLong = saturnData?.longitude || 0;
  const uranusLong = uranusData?.longitude || 0;
  const neptuneLong = neptuneData?.longitude || 0;
  const plutoLong = plutoData?.longitude || 0;

  // Get zodiac signs (already calculated by astronomy engine)
  const sunSign = { sign: sunData?.sign || 'Aries', degree: sunData?.degree || 0 };
  const moonSign = { sign: moonData?.sign || 'Aries', degree: moonData?.degree || 0 };
  const mercurySign = { sign: mercuryData?.sign || 'Aries', degree: mercuryData?.degree || 0 };
  const venusSign = { sign: venusData?.sign || 'Aries', degree: venusData?.degree || 0 };
  const marsSign = { sign: marsData?.sign || 'Aries', degree: marsData?.degree || 0 };
  const jupiterSign = { sign: jupiterData?.sign || 'Aries', degree: jupiterData?.degree || 0 };
  const saturnSign = { sign: saturnData?.sign || 'Aries', degree: saturnData?.degree || 0 };
  const uranusSign = { sign: uranusData?.sign || 'Aries', degree: uranusData?.degree || 0 };
  const neptuneSign = { sign: neptuneData?.sign || 'Aries', degree: neptuneData?.degree || 0 };
  const plutoSign = { sign: plutoData?.sign || 'Aries', degree: plutoData?.degree || 0 };
  
  // Calculate Ascendant (if location provided)
  let ascendant = { sign: 'Aries' as ZodiacSign, degree: 0, longitude: 0 };
  let houses: Array<{ house: House; sign: ZodiacSign; degree: number; longitude: number }> = [];
  
  if (birthLocation) {
    // Validate location data
    if (isNaN(birthLocation.latitude) || isNaN(birthLocation.longitude)) {
      console.warn('Invalid birth location coordinates:', birthLocation);
    } else if (birthLocation.latitude < -90 || birthLocation.latitude > 90) {
      console.warn('Invalid latitude (must be -90 to 90):', birthLocation.latitude);
    } else if (birthLocation.longitude < -180 || birthLocation.longitude > 180) {
      console.warn('Invalid longitude (must be -180 to 180):', birthLocation.longitude);
    } else {
      console.log('🔍 [NATAL CHART] About to calculate ascendant with:', {
        utcDate: date.toISOString(),
        jdApprox: (date.getTime() / 86400000) + 2440587.5,
        latitude: birthLocation.latitude,
        longitude: birthLocation.longitude,
        longitudeSign: birthLocation.longitude >= 0 ? 'East' : 'West'
      });
      
      try {
        ascendant = await calculateAscendant(date, birthLocation.latitude, birthLocation.longitude);
        houses = await calculateHouseCusps(date, birthLocation.latitude, birthLocation.longitude, houseSystem);
        
        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Ascendant calculated:', {
            sign: ascendant.sign,
            degree: formatAstrologyDegree(ascendant.degree),
            longitude: ascendant.longitude,
            date: date.toISOString(),
            location: birthLocation
          });
          
          console.log('🏠 Houses calculated:', {
            system: houseSystem,
            house1: `${houses[0]?.sign} ${formatAstrologyDegree(houses[0]?.degree)} (${houses[0]?.longitude.toFixed(2)}°)`,
            house2: `${houses[1]?.sign} ${formatAstrologyDegree(houses[1]?.degree)} (${houses[1]?.longitude.toFixed(2)}°)`,
            house7: `${houses[6]?.sign} ${formatAstrologyDegree(houses[6]?.degree)} (${houses[6]?.longitude.toFixed(2)}°)`,
            house10: `${houses[9]?.sign} ${formatAstrologyDegree(houses[9]?.degree)} (${houses[9]?.longitude.toFixed(2)}°)`,
            allHouses: houses.map((h, i) => `H${i+1}: ${h.sign} ${formatAstrologyDegree(h.degree)} (${h.longitude.toFixed(2)}°)`)
          });
        }
      } catch (error) {
        console.error('Error calculating ascendant:', error);
      }
    }
  } else {
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️ No birth location provided - ascendant cannot be calculated');
    }
  }
  
  // Create planet positions
  const planetPositions: PlanetPosition[] = [
    {
      planet: 'Sun',
      sign: sunSign.sign as ZodiacSign,
      degree: sunSign.degree,
      house: birthLocation ? getPlanetHouse(sunLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Moon',
      sign: moonSign.sign as ZodiacSign,
      degree: moonSign.degree,
      house: birthLocation ? getPlanetHouse(moonLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Mercury',
      sign: mercurySign.sign as ZodiacSign,
      degree: mercurySign.degree,
      house: birthLocation ? getPlanetHouse(mercuryLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Venus',
      sign: venusSign.sign as ZodiacSign,
      degree: venusSign.degree,
      house: birthLocation ? getPlanetHouse(venusLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Mars',
      sign: marsSign.sign as ZodiacSign,
      degree: marsSign.degree,
      house: birthLocation ? getPlanetHouse(marsLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Jupiter',
      sign: jupiterSign.sign as ZodiacSign,
      degree: jupiterSign.degree,
      house: birthLocation ? getPlanetHouse(jupiterLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Saturn',
      sign: saturnSign.sign as ZodiacSign,
      degree: saturnSign.degree,
      house: birthLocation ? getPlanetHouse(saturnLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Uranus',
      sign: uranusSign.sign as ZodiacSign,
      degree: uranusSign.degree,
      house: birthLocation ? getPlanetHouse(uranusLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Neptune',
      sign: neptuneSign.sign as ZodiacSign,
      degree: neptuneSign.degree,
      house: birthLocation ? getPlanetHouse(neptuneLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Pluto',
      sign: plutoSign.sign as ZodiacSign,
      degree: plutoSign.degree,
      house: birthLocation ? getPlanetHouse(plutoLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    }
  ];
  
  // Calculate aspects
  const planetLongitudes = planetPositions.map((p, idx) => ({
    planet: p.planet,
    longitude: [sunLong, moonLong, mercuryLong, venusLong, marsLong, jupiterLong, saturnLong, uranusLong, neptuneLong, plutoLong][idx]
  }));
  const aspects = calculateAspects(planetLongitudes);
  
  // Calculate elemental balance
  const elements = planetPositions.map(p => {
    const signKey = p.sign.toLowerCase() as keyof typeof ZODIAC_SIGNS;
    return ZODIAC_SIGNS[signKey]?.element || 'Fire';
  });
  
  const elementalBalance = {
    fire: elements.filter(e => e === 'Fire').length,
    earth: elements.filter(e => e === 'Earth').length,
    air: elements.filter(e => e === 'Air').length,
    water: elements.filter(e => e === 'Water').length
  };
  
  // Calculate modality balance
  const modalities = planetPositions.map(p => {
    const signKey = p.sign.toLowerCase() as keyof typeof ZODIAC_SIGNS;
    return ZODIAC_SIGNS[signKey]?.modality || 'Cardinal';
  });
  
  const modalityBalance = {
    cardinal: modalities.filter(m => m === 'Cardinal').length,
    fixed: modalities.filter(m => m === 'Fixed').length,
    mutable: modalities.filter(m => m === 'Mutable').length
  };
  
  return {
    birthDate: date.toISOString().split('T')[0],
    birthTime,
    birthLocation,
    planets: planetPositions,
    ascendant,
    houses,
    aspects,
    elementalBalance,
    modalityBalance
  };
}

/**
 * Fallback natal chart calculator using VSOP87 synchronous calculations
 * Used when astronomy-calculations.js is not available
 */
async function calculateCompleteNatalChartFallback(
  date: Date,
  birthTime?: string,
  birthLocation?: { latitude: number; longitude: number; city?: string },
  houseSystem: HouseSystem = 'equal'
): Promise<CompleteNatalChart> {
  console.warn('⚠️ Using fallback VSOP87 calculations - astronomy-calculations.js not available');
  
  // Calculate all planetary positions using VSOP87-based functions
  const sunLong = calculateSunPosition(date);
  const moonLong = calculateMoonPosition(date);
  const mercuryLong = calculateMercuryPosition(date);
  const venusLong = calculateVenusPosition(date);
  const marsLong = calculateMarsPosition(date);
  const jupiterLong = calculateJupiterPosition(date);
  const saturnLong = calculateSaturnPosition(date);
  const uranusLong = calculateUranusPosition(date);
  const neptuneLong = calculateNeptunePosition(date);
  const plutoLong = calculatePlutoPosition(date);
  
  // Get zodiac signs for all planets
  const sunSign = getZodiacSign(sunLong);
  const moonSign = getZodiacSign(moonLong);
  const mercurySign = getZodiacSign(mercuryLong);
  const venusSign = getZodiacSign(venusLong);
  const marsSign = getZodiacSign(marsLong);
  const jupiterSign = getZodiacSign(jupiterLong);
  const saturnSign = getZodiacSign(saturnLong);
  const uranusSign = getZodiacSign(uranusLong);
  const neptuneSign = getZodiacSign(neptuneLong);
  const plutoSign = getZodiacSign(plutoLong);
  
  // Calculate Ascendant (if location provided)
  let ascendant = { sign: 'Aries' as ZodiacSign, degree: 0, longitude: 0 };
  let houses: Array<{ house: House; sign: ZodiacSign; degree: number; longitude: number }> = [];
  
  if (birthLocation) {
    try {
      ascendant = await calculateAscendant(date, birthLocation.latitude, birthLocation.longitude);
      houses = await calculateHouseCusps(date, birthLocation.latitude, birthLocation.longitude, houseSystem);
    } catch (error) {
      console.error('Error calculating ascendant:', error);
    }
  }
  
  // Create planet positions
  const planetPositions: PlanetPosition[] = [
    {
      planet: 'Sun',
      sign: sunSign.sign as ZodiacSign,
      degree: sunSign.degree,
      house: birthLocation ? getPlanetHouse(sunLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Moon',
      sign: moonSign.sign as ZodiacSign,
      degree: moonSign.degree,
      house: birthLocation ? getPlanetHouse(moonLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Mercury',
      sign: mercurySign.sign as ZodiacSign,
      degree: mercurySign.degree,
      house: birthLocation ? getPlanetHouse(mercuryLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Venus',
      sign: venusSign.sign as ZodiacSign,
      degree: venusSign.degree,
      house: birthLocation ? getPlanetHouse(venusLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Mars',
      sign: marsSign.sign as ZodiacSign,
      degree: marsSign.degree,
      house: birthLocation ? getPlanetHouse(marsLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Jupiter',
      sign: jupiterSign.sign as ZodiacSign,
      degree: jupiterSign.degree,
      house: birthLocation ? getPlanetHouse(jupiterLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Saturn',
      sign: saturnSign.sign as ZodiacSign,
      degree: saturnSign.degree,
      house: birthLocation ? getPlanetHouse(saturnLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Uranus',
      sign: uranusSign.sign as ZodiacSign,
      degree: uranusSign.degree,
      house: birthLocation ? getPlanetHouse(uranusLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Neptune',
      sign: neptuneSign.sign as ZodiacSign,
      degree: neptuneSign.degree,
      house: birthLocation ? getPlanetHouse(neptuneLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    },
    {
      planet: 'Pluto',
      sign: plutoSign.sign as ZodiacSign,
      degree: plutoSign.degree,
      house: birthLocation ? getPlanetHouse(plutoLong, houses.map(h => ({ house: h.house, longitude: h.longitude }))) : undefined
    }
  ];
  
  // Calculate aspects
  const planetLongitudes = planetPositions.map((p, idx) => ({
    planet: p.planet,
    longitude: [sunLong, moonLong, mercuryLong, venusLong, marsLong, jupiterLong, saturnLong, uranusLong, neptuneLong, plutoLong][idx]
  }));
  const aspects = calculateAspects(planetLongitudes);
  
  // Calculate elemental balance
  const elements = planetPositions.map(p => {
    const signKey = p.sign.toLowerCase() as keyof typeof ZODIAC_SIGNS;
    return ZODIAC_SIGNS[signKey]?.element || 'Fire';
  });
  
  const elementalBalance = {
    fire: elements.filter(e => e === 'Fire').length,
    earth: elements.filter(e => e === 'Earth').length,
    air: elements.filter(e => e === 'Air').length,
    water: elements.filter(e => e === 'Water').length
  };
  
  // Calculate modality balance
  const modalities = planetPositions.map(p => {
    const signKey = p.sign.toLowerCase() as keyof typeof ZODIAC_SIGNS;
    return ZODIAC_SIGNS[signKey]?.modality || 'Cardinal';
  });
  
  const modalityBalance = {
    cardinal: modalities.filter(m => m === 'Cardinal').length,
    fixed: modalities.filter(m => m === 'Fixed').length,
    mutable: modalities.filter(m => m === 'Mutable').length
  };
  
  return {
    birthDate: date.toISOString().split('T')[0],
    birthTime,
    birthLocation,
    planets: planetPositions,
    ascendant,
    houses,
    aspects,
    elementalBalance,
    modalityBalance
  };
}


